import React, { useState } from "react";
import { Dimensions, StyleSheet, View } from "react-native";
import { Modal, Text, TextInput, TouchableOpacity } from "react-native";
import { ScrollView } from "react-native-gesture-handler";
import {
  addTrackerOrderEnquiry,
  addTrackerOrderEnquiryReply,
} from "../../../redux/dispatcher/chinaTrackerDispatcher";
import { useToast } from "react-native-toast-notifications";
import Spinner from "../../../components/shared/Spinner";
import { useForm, Controller } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";

interface RequestETAModalProps {
  visible: boolean;
  onClose: any;
  order: any;
  rootOrder: any;
}

const RequestETAModal: React.FC<RequestETAModalProps> = ({
  visible,
  onClose,
  order,
  rootOrder,
}: any) => {
  const dispatch = useDispatch<any>();
  const { loadingAddEnqiry } = useSelector((state: any) => state.chinaTracker);
  const toast = useToast();

  // Hook form setup
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const onSubmit = (data: any) => {
    const payload = {
      orderNumber: order.orderNumber,
      customerName: rootOrder.BPName,
      itemCode: rootOrder.ItemCode,
      itemName: rootOrder.ItemName,
      orderDate: rootOrder.OrderDate,
      subject: `${order.subject}`,
      priority: order.priority, // Default priority for ETA requests
      message: data.message,
      key: rootOrder.Key,
      enquiryRef: order._id,
      enquiryTrackerType: "CHINA_TRACKER",
    };

    dispatch(addTrackerOrderEnquiryReply(payload))
      .unwrap()
      .then(() =>
        toast.show("ETA reply sent successfully", {
          type: "success",
        })
      )
      .catch((err: any) =>
        toast.show(err.message || err || "Error sending ETA request", {
          type: "danger",
        })
      );

    onClose();
  };

  return (
    <Modal visible={visible} transparent animationType="slide">
      <View style={styles.overlay}>
        <View style={styles.modalContainer}>
          <ScrollView
            contentContainerStyle={styles.scrollContent}
            showsVerticalScrollIndicator={false}
          >
            {/* Header with back arrow and title */}
            <View style={styles.header}>
              <TouchableOpacity onPress={onClose} style={styles.backButton}>
                <Text style={styles.backArrow}>←</Text>
              </TouchableOpacity>
              <Text style={styles.title}>Re-{order.subject}</Text>
            </View>

            {loadingAddEnqiry ? (
              <Spinner
                size="small"
                color="#74BBFF"
                styles={{
                  flexDirection: "row",
                  marginTop: "5%",
                  marginBottom: "5%",
                }}
              />
            ) : (
              <>
                {/* Order Details */}
                <View style={styles.detailsContainer}>
                  <View style={styles.detailRow}>
                    <Text style={styles.label}>Order No:</Text>
                    <Text style={styles.value}>{order?.orderNumber || ""}</Text>
                  </View>

                  <View style={styles.detailRow}>
                    <Text style={styles.label}>MS:</Text>
                    <Text style={styles.value}>{rootOrder?.MSRef || ""}</Text>
                  </View>

                  <View style={styles.detailRow}>
                    <Text style={styles.label}>State:</Text>
                    <Text style={styles.value}>
                      {rootOrder?.StateDelivery || ""}
                    </Text>
                  </View>

                  <View style={styles.detailRow}>
                    <Text style={styles.label}>PO Order Date:</Text>
                    <Text style={styles.value}>
                      {rootOrder?.OrderDate || ""}
                    </Text>
                  </View>

                  <View style={styles.detailRow}>
                    <Text style={styles.label}>Priority Level:</Text>
                    <Text style={styles.value}>{order?.priority}</Text>
                  </View>
                </View>

                {/* Message Input */}
                <View style={styles.messageSection}>
                  <Text style={styles.inputLabel}>Message</Text>
                  <Controller
                    control={control}
                    name="message"
                    rules={{ required: "Message is required" }}
                    render={({ field: { onChange, value } }) => (
                      <>
                        <TextInput
                          style={styles.messageInput}
                          multiline
                          placeholder="Type your message..."
                          value={value}
                          onChangeText={onChange}
                          textAlignVertical="top"
                        />
                        {errors.message && (
                          <Text style={styles.errorText}>
                            {errors.message.message as string}
                          </Text>
                        )}
                      </>
                    )}
                  />
                </View>

                {/* Send Button */}
                <View style={styles.footer}>
                  <TouchableOpacity
                    style={styles.sendButton}
                    onPress={handleSubmit(onSubmit)}
                  >
                    <Text style={styles.sendText}>Send</Text>
                  </TouchableOpacity>
                </View>
              </>
            )}
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
};

const { width, height } = Dimensions.get("window");

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.5)",
    justifyContent: "center",
    alignItems: "center",
    padding: 20,
  },
  modalContainer: {
    backgroundColor: "#fff",
    width: "60%",
    height: "60%",
    borderRadius: 10,
    paddingHorizontal: 20,
    paddingVertical: 20,
  },
  scrollContent: {
    flexGrow: 1,
    paddingBottom: 20,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 30,
    paddingBottom: 10,
  },
  backButton: {
    marginRight: 15,
    padding: 5,
  },
  backArrow: {
    fontSize: 24,
    fontWeight: "bold",
    color: "#000",
  },
  title: {
    fontWeight: "600",
    flex: 1,
    color: "#000",
    fontSize: 18,
  },
  detailsContainer: {
    marginBottom: 25,
  },
  detailRow: {
    flexDirection: "row",
    marginBottom: 12,
    alignItems: "flex-start",
  },
  label: {
    fontWeight: "600",
    color: "#000",
    width: width > 400 ? 140 : 110,
    flexShrink: 0,
  },
  value: {
    fontSize: 16,
    color: "#000",
    flex: 1,
    marginLeft: 10,
  },
  messageSection: {
    flex: 1,
    marginBottom: 20,
  },
  inputLabel: {
    fontWeight: "600",
    fontSize: 16,
    marginBottom: 10,
    color: "#000",
  },
  messageInput: {
    flex: 1,
    minHeight: 150,
    fontSize: 16,
    borderWidth: 1,
    borderColor: "#ddd",
    borderRadius: 8,
    padding: 15,
    textAlignVertical: "top",
    backgroundColor: "#fafafa",
  },
  footer: {
    alignItems: "flex-end",
    marginTop: 10,
  },
  sendButton: {
    backgroundColor: "#666",
    paddingHorizontal: 30,
    paddingVertical: 12,
    borderRadius: 6,
    minWidth: 80,
  },
  sendText: {
    color: "#fff",
    fontWeight: "600",
    textAlign: "center",
    fontSize: 16,
  },
  errorText: {
    color: "red",
    fontSize: 12,
    marginTop: 5,
  },
});

export default RequestETAModal;
